/**
 * An invite token, captured the instant the page loads.
 *
 * THIS RUNS AT IMPORT TIME, and that is the whole point. Two things race to
 * destroy `/join/<token>` before any React effect can read it:
 *
 *   1. App's canonicalising effect. `parseRoute` does not know `/join`, so it
 *      returns null, and that effect `replaceState`s the URL to `/expenses`
 *      on mount. The token is gone from the address bar before the first
 *      effect that wanted it ever runs.
 *
 *   2. The person is not signed in yet — which is the NORMAL case for someone
 *      being invited — so any effect gated on having an account returns early
 *      on that first pass and never looks.
 *
 * Both were true, so the token was lost twice over and the link silently did
 * nothing. Reading it at module scope happens before React mounts at all,
 * which is earlier than either.
 *
 * `sessionStorage`, not the URL: it should not end up in a bookmark, a history
 * entry or a referrer header, and it should die with the tab if the person
 * changes their mind.
 */
const KEY = 'et.invite';

function capture(): void {
  const match = /^\/join\/([^/?#]+)/.exec(window.location.pathname);
  if (!match?.[1]) return;
  try {
    sessionStorage.setItem(KEY, match[1]);
  } catch {
    /* private mode: the token is still in the URL for this first pass */
  }
}

capture();

export function pendingInvite(): string | null {
  // The URL first, for the private-mode case where the write above failed.
  const match = /^\/join\/([^/?#]+)/.exec(window.location.pathname);
  if (match?.[1]) return match[1];
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearPendingInvite(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
